import { useQuery } from '@tanstack/react-query';
import { TZDate } from '@date-fns/tz';
import { useApi, useAuth } from '../../hooks';
import { CareModel } from '../../models/user';
import { formatDateToDayOnly } from '../../components/AppointmentsPage/utils';
import {
  AvailabilityEntry,
  MeetingInfo,
  MeetingType,
} from '../../models/meeting';
import { ProviderType } from '../types';
import qs from 'qs';

type ProviderInfo = {
  availability: AvailabilityEntry[];
  display_name: string;
  image_url: string;
  provider_id: number;
};

type ProviderAvailabilityResponse = {
  meeting_type: MeetingInfo;
  providers: ProviderInfo[];
};

type Props = {
  care_model?: CareModel;
  endDate?: TZDate;
  meetingType?: MeetingType | null;
  providerType?: ProviderType | null;
  startDate?: TZDate;
  timezone?: string;
  userId?: string;
};

export const useProviderAvailability = ({
  care_model,
  endDate,
  meetingType: meeting_type,
  providerType,
  startDate,
  timezone,
  userId: user_id,
}: Props) => {
  const { api } = useApi();
  const { userToken } = useAuth();

  const fetchAvailability = async (): Promise<ProviderAvailabilityResponse> => {
    if (!startDate || !endDate) {
      return { availability: [] } as unknown as ProviderAvailabilityResponse;
    }

    const start_date = formatDateToDayOnly(startDate);
    const end_date = formatDateToDayOnly(endDate);
    const care_member_category = providerType?.toLowerCase() ?? null;

    const queryStr = qs.stringify({
      care_member_category,
      end_date,
      meeting_type,
      start_date,
      timezone,
      user_id,
    });

    return api({
      route: `/private/coach/provider/availability${
        queryStr ? `?${queryStr}` : ''
      }`,
      token: userToken,
    });
  };

  const { data, isInitialLoading, isLoading, isError } = useQuery<
    ProviderAvailabilityResponse,
    Error
  >({
    queryKey: [
      'provider-availability',
      user_id,
      endDate,
      meeting_type,
      startDate,
      timezone,
    ],
    queryFn: fetchAvailability,
    enabled:
      !!care_model &&
      !!user_id &&
      !!endDate &&
      !!meeting_type &&
      !!providerType &&
      !!startDate &&
      !!timezone,
    refetchOnWindowFocus: false,
  });

  return {
    isLoading: isInitialLoading || isLoading,
    isError,
    data,
  };
};
